import React, { FC, useContext } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ButtonComponentRenderer } from '@agria/theme-brochureware/src/components/componentRenderer/ButtonComponentRenderer';
import { Stack } from '@agria/theme/src/components/stack/Stack';
import {
  HeroProps,
  StyledHeroProps,
} from '@agria/theme/src/components/hero/Hero';
import {
  createMarkup,
  useBackgroundColor,
  getHighlightColor,
} from '@agria/theme/src/utils';
import { ExperimentsContext } from '@agria/theme/src/context/ExperimentsContext';
import ikcLogoSrc from '@agria/theme/src/images/logos/IKC-Logo.jpg';
import telegraphImageSrc from '@agria/theme/src/images/logos/telegraph-best-buy-logo.png';
import ecoOffsetImageSrc from '@agria/theme/src/images/logos/ecoOffsetLogo.png';
import { siteLinks } from '@agria/theme/src/const/siteLinks';
import { Image } from '@agria/theme/src/components/Image/Image';
import { Typography } from '@agria/theme/src/components/typography/Typography';
import { TrustpilotStarsStacked } from '@agria/theme/src/components/trustpilotStarsStacked/TrustpilotStarsStacked';
import { DefaqtoStars } from '@agria/theme/src/components/defaqtoStars/DefaqtoStars';
import { LinkTo } from '@agria/theme/src/components/linkTo/LinkTo';
import { TrustpilotStars } from '@agria/theme/src/components/trustpilotStars/TrustpilotStars';
import { IconList } from '@agria/theme/src/components/iconList/IconList';
import { useSiteCountry } from '@agria/theme/src/hooks/useSiteCountry';
import { ArrowLeft } from '../icon';
import tokens from '../hero/tokens';

const StyledSuperTitle = styled(Typography)`
  ${() => css`
    a {
      ${useBackgroundColor('brandOne100')}
      text-decoration: none;
    }
  `};
`;

const StyledLogos = styled.div`
  ${({ theme: { space, media } }) => css`
    display: flex;
    justify-content: ${tokens.allLogos ? 'space-between' : 'flex-start'};
    align-items: center;
    gap: ${space.xSmall};

    @media (min-width: ${media.medium}) {
      gap: ${tokens.allLogos ? space.xxxSmall : space.xSmall};
    }
  `};
`;

const StyledLogosIreland = styled.div`
  ${({ theme: { space } }) => css`
    align-items: center;
    display: flex;
    justify-content: flex-start;
    gap: ${space.xSmall};

    > div {
      flex-basis: 100px;
    }
  `};
`;

const StyledTrustpilotStarsStacked = styled(TrustpilotStarsStacked)`
  flex-basis: 104px;
`;

const StyledDefaqtoStars = styled(DefaqtoStars)`
  flex-basis: 96px;
`;

const StyledTelegraphImage = styled(Image, {
  shouldForwardProp: (prop) => prop !== '$AGRIA108',
})<{ $AGRIA108: string }>`
  ${({ theme: { maxMedia }, $AGRIA108 }) => css`
    ${!$AGRIA108 &&
    css`
      @media (max-width: ${maxMedia.medium}) {
        display: none;
      }
    `}
  `};
`;

const StyledEcoOffsetImage = styled(Image, {
  shouldForwardProp: (prop) => prop !== '$AGRIA108',
})<{ $AGRIA108: string }>`
  ${({ theme: { maxMedia }, $AGRIA108 }) => css`
    height: auto;

    ${$AGRIA108 &&
    css`
      @media (max-width: ${maxMedia.medium}) {
        display: none;
      }
    `}
  `};
`;

const StyledReturnLink = styled(LinkTo)`
  ${({ theme: { space } }) => css`
    align-items: center;
    display: flex;
    gap: ${space.xxSmall};
  `};
`;

const StyledHeroContent = styled.div<StyledHeroProps>`
  ${({
    theme: { colors, space, media, borderRadii },
    jumbo,
    borderColor,
    fullWidth,
  }) => {
    const {
      heroBackground,
      heroBackgroundDesktop,
      heroBodyBorder,
      heroPaddingHorizontal,
      heroPaddingVertical,
      includeImageOverlay,
    } = tokens;

    const padding = jumbo
      ? `${space.xLarge} ${space.medium}`
      : `${space.large} ${space.medium}`;

    const paddingDesktop = jumbo
      ? `${space.xxLarge} ${space[heroPaddingHorizontal]}`
      : `${space[heroPaddingVertical]} ${space[heroPaddingHorizontal]}`;

    return css`
      ${useBackgroundColor(heroBackground)}
      margin-inline: -${space.medium};
      padding: ${includeImageOverlay
        ? `${space.large} ${space.medium}`
        : padding};
      display: grid;
      gap: ${space.large};

      ${heroBodyBorder &&
      css`
        border-top: ${space.xxSmall} solid ${colors.brandOne100};
      `}

      ${borderColor &&
      css`
        border-top: ${space.xxSmall} solid ${colors[borderColor]};
      `}

      ${fullWidth &&
      !includeImageOverlay &&
      css`
        border-radius: ${borderRadii.medium};
      `}

      @media (min-width: ${media.medium}) {
        margin-inline: 0;
        width: 544px;
        padding: ${paddingDesktop};
        border-radius: ${borderRadii.medium};

        ${includeImageOverlay &&
        css`
          ${useBackgroundColor(heroBackgroundDesktop)};
        `}
      }
    `;
  }};
`;

export const HeroContent: FC<HeroProps> = ({
  buttonOne,
  colorCode = 'none',
  description,
  fullWidth,
  heading,
  heroBodyComponent,
  includeDiscountTerms,
  jumbo,
  logos,
  returnLink,
  small,
  superTitle,
  trustpilotStars,
  isHomepage = false,
}) => {
  const { experiments } = useContext(ExperimentsContext);
  const { isIe } = useSiteCountry();

  return (
    <StyledHeroContent
      fullWidth={fullWidth}
      jumbo={jumbo}
      borderColor={
        colorCode !== 'none' ? getHighlightColor(colorCode) : undefined
      }
    >
      {(superTitle || heading || description || returnLink) && (
        <Stack gap={small ? 'medium' : 'small'}>
          {returnLink && (
            <StyledReturnLink onClick={returnLink.onClick} to={returnLink.url}>
              <ArrowLeft size="medium" />
              <Typography component="span" variant="bodyMedium">
                {returnLink.text}
              </Typography>
            </StyledReturnLink>
          )}
          <Stack gap="xxSmall">
            {superTitle && (
              <StyledSuperTitle
                component="div"
                variant="bodyLarge"
                data-test="hero-super-title"
                role="heading"
                aria-level={2}
              >
                {superTitle}
              </StyledSuperTitle>
            )}
            {heading && (
              <Typography
                component="h1"
                variant={small ? 'headingLarge' : 'headingXLarge'}
                data-test="hero-title"
              >
                {heading}
              </Typography>
            )}
          </Stack>
          {description && (
            <Typography
              component="p"
              variant={tokens.heroDescriptionVariant}
              data-test="hero-description"
            >
              {createMarkup(description)}
            </Typography>
          )}
          {includeDiscountTerms && (
            <Typography
              component="p"
              variant="bodySmall"
              data-test="hero-terms"
            >
              <LinkTo to={siteLinks.termsAndConditions}>
                Terms and conditions apply
              </LinkTo>
            </Typography>
          )}
        </Stack>
      )}

      {heroBodyComponent}

      {((buttonOne && buttonOne.length) ||
        logos === 'Trustpilot rating' ||
        logos === 'Trust logos' ||
        trustpilotStars) && (
        <Stack gap="fluidXLarge">
          {buttonOne && buttonOne.length > 0 && (
            <ButtonComponentRenderer components={[buttonOne[0].component]} />
          )}
          {(logos === 'Trustpilot rating' || trustpilotStars) && (
            <TrustpilotStars />
          )}

          {logos === 'Trust logos' && (
            <StyledLogos>
              <StyledTrustpilotStarsStacked />
              <StyledDefaqtoStars />
              {tokens.allLogos && (
                <>
                  <StyledTelegraphImage
                    src={telegraphImageSrc}
                    alt="Telegraph Best Buy logo"
                    loading="eager"
                    width={83}
                    height={49}
                    $AGRIA108={experiments.AGRIA108}
                  />
                  <StyledEcoOffsetImage
                    src={ecoOffsetImageSrc}
                    alt="Eco Offset - Carbon positive company logo"
                    loading="eager"
                    width={64}
                    height={64}
                    style={{ height: 'auto' }}
                    $AGRIA108={experiments.AGRIA108}
                  />
                </>
              )}
            </StyledLogos>
          )}
        </Stack>
      )}

      {isHomepage && isIe && (
        <StyledLogosIreland>
          <Image
            alt="Irish Kennel Club"
            // height={108}
            loading="eager"
            src={ikcLogoSrc}
            // width={118}
          />

          <TrustpilotStarsStacked />
        </StyledLogosIreland>
      )}
    </StyledHeroContent>
  );
};
